import loadingBars from '@fd-assets/core-styles/client/loadingBars';

import errorNotification from '@grins/fd-modules/bonz/modules/errorNotification';
import utils from '@grins/fd-modules/bonz/modules/utils';

import bonzConsts from '@grins/fd-modules/bonz/shared/constants';

import constants from '@grins/fd-modules/Fandango/constants';

import inputValidator from '@grins/fd-modules/shared/inputValidator';
import phoneFormatter from '@grins/fd-modules/shared/phoneFormatter';

import {
  Fandango_adobeLaunch as Fandango_adobeLaunch_track,
  Fandango_xhr as xhr,
  fd_url_manager as links,
  focusableInput_form as focusableForm
} from '@grins/fd-modules/splits/onering-global';

type JoinFields = HTMLFormControlsCollection & {
  email: HTMLInputElement;
  firstName: HTMLInputElement;
  password: HTMLInputElement;
  phoneNumber?: HTMLInputElement;
  smsConsent?: HTMLInputElement;
};

const { track } = Fandango_adobeLaunch_track;

const Selectors = {
  FORM: '#join-now-form',
  SAVE_BTN: '#join-now-submit-btn',
  PHONE_INPUT: '#phone-number',
  PHONE_FIELD: '.join-now__phone-field',
  PHONE_ERROR: '.join-now__phone-field .js-fcsbl-input-error',
  PHONE_ERROR_MSG: '.join-now__phone-field .js-fcsbl-input-error-msg',
  SMS_CONSENT_ERROR: '.js-sms-consent-error',
  SMS_CONSENT_ERROR_MSG: '.join-now__error-text',
  SMS_CONSENT: '#sms-consent',
};

const Styles = {
  LOADING_FULL: 'btn--loading-wrap',
  HIDE: 'hide',
  HAS_ERROR: 'fcsbl-input--has-error',
  ERROR: 'fcsbl-input__error',
};

const Msgs = {
  GENERIC_ERROR: 'Something went wrong on our end. Please try again later.',
  DUPE_EMAIL: 'Welcome back! That Fandango account already exists, you can <strong><a href="{{url}}" class="validation-tip__link">sign in</a></strong> to your account.',
};

function setSaving(isSaving: boolean) {
  const saveBtn = document.querySelector(Selectors.SAVE_BTN);

  if (isSaving) {
    loadingBars.start(saveBtn, { className: Styles.LOADING_FULL });
  } else {
    loadingBars.stop(saveBtn);
  }
}

function showCheckboxError(message: string) {
  const errorContainer = document.querySelector(Selectors.SMS_CONSENT_ERROR);
  const errorMsg = document.querySelector(Selectors.SMS_CONSENT_ERROR_MSG);

  if (errorContainer && errorMsg) {
    errorMsg.textContent = message;
    errorContainer.classList.remove(Styles.HIDE);
  }

  // Focus the checkbox when error is shown
  const smsCheckbox = document.querySelector(Selectors.SMS_CONSENT) as HTMLInputElement;

  if (smsCheckbox) {
    smsCheckbox.focus();
  }
}

function hideCheckboxError() {
  const errorContainer = document.querySelector(Selectors.SMS_CONSENT_ERROR);

  if (errorContainer) {
    errorContainer.classList.add(Styles.HIDE);
  }
}

function showPhoneError(message: string) {
  const errorContainer = document.querySelector(Selectors.PHONE_ERROR);
  const errorMsg = document.querySelector(Selectors.PHONE_ERROR_MSG);
  const phoneField = document.querySelector(Selectors.PHONE_FIELD);
  const phoneInput = document.querySelector(Selectors.PHONE_INPUT) as HTMLInputElement;

  if (errorContainer && errorMsg) {
    errorMsg.textContent = message;
    errorContainer.classList.remove(Styles.HIDE);
  }

  if (phoneField) {
    phoneField.classList.add(Styles.HAS_ERROR);
  }

  if (phoneInput) {
    phoneInput.classList.add(Styles.ERROR);

    // Focus the phone input when error is shown
    phoneInput.focus();
  }
}

function hidePhoneError() {
  const errorContainer = document.querySelector(Selectors.PHONE_ERROR);
  const phoneField = document.querySelector(Selectors.PHONE_FIELD);
  const phoneInput = document.querySelector(Selectors.PHONE_INPUT);

  if (errorContainer) {
    errorContainer.classList.add(Styles.HIDE);
  }

  if (phoneField) {
    phoneField.classList.remove(Styles.HAS_ERROR);
  }

  if (phoneInput) {
    phoneInput.classList.remove(Styles.ERROR);
  }
}

function onFail(xhr: { responseJSON?: { msg?: string }; status?: number } = {}) {
  setSaving(false);
  const { responseJSON, status } = xhr;

  if (status === constants.HTTPStatusCodes.CONFLICT) {
    return errorNotification.show(responseJSON?.msg ?? '');
  }

  trackError(Msgs.GENERIC_ERROR);
  errorNotification.show(Msgs.GENERIC_ERROR);
}

function onDone(response: { success: boolean }, email: string, password: string) {
  setSaving(false);

  if (!response || !response.success) {
    return trackError(Msgs.GENERIC_ERROR);
  }

  track(bonzConsts.OmnitureEvents.SIGNUP_COMPLETE);
  utils.addFavorite();
  utils.saveAuthenticationToApp(email, password, 'join');
  setTimeout(() => window.location.href = utils.getDestinationURI(), 250);
}

function trackError(msg: string) {
  track({ event: bonzConsts.OmnitureEvents.SIGNUP_ERROR, errorMessage: msg });
}

function validateFields(fields: JoinFields) {
  const { ValidationTypes, validate } = inputValidator;
  const { email, firstName, password } = fields;

  const validationRules = [{
    input: email,
    isRequired: true,
    type: ValidationTypes.EMAIL
  }, {
    input: firstName,
    isRequired: true,
    type: ValidationTypes.NAMES
  },
  {
    input: password,
    isRequired: true,
    type: ValidationTypes.PASSWORD
  }];

  const { hasErrors, errors } = validate(validationRules);
  return { errors, hasErrors, msg: !hasErrors ? '' : errors.map(({ error }) => error).join('<br>') };
}

async function onSubmit(this: HTMLFormElement, e: SubmitEvent) {
  e.preventDefault();
  errorNotification.hide();
  hideCheckboxError();
  hidePhoneError();
  setSaving(true);
  const fields = this.elements as JoinFields;
  const { hasErrors, errors } = validateFields(fields);

  if (hasErrors) {
    setSaving(false);
    return focusableForm.toggleErrors(errors ?? [], true);
  }

  const email: string = fields.email.value;
  const password: string = fields.password.value;

  // Get phone and consent values
  const phoneValue = fields.phoneNumber?.value ? phoneFormatter.stripNonNumeric(fields.phoneNumber.value) : '';
  const smsConsentChecked = Boolean(fields.smsConsent?.checked);

  if (phoneValue && phoneValue.length !== 10) {
    setSaving(false);
    return showPhoneError('Please enter a valid 10-digit mobile number.');
  }

  // Validate SMS opt-in requirements
  if (phoneValue && !smsConsentChecked) {
    setSaving(false);
    return showCheckboxError('Please consent to receive marketing text/SMS messages.');
  }

  if (smsConsentChecked && !phoneValue) {
    setSaving(false);
    return showPhoneError('Please enter a mobile number.');
  }

  const data = {
    email,
    firstName: fields.firstName.value,
    password,
    phoneNumber: phoneValue,
    smsConsent: smsConsentChecked
  };

  xhr.ajax({ url: links.snapiBonzJoin(), method: 'POST', data })
    ?.done((response: { success: boolean }) => onDone(response, email, password))
    .fail(onFail);
}

function init() {
  const form = document.querySelector<HTMLFormElement>(Selectors.FORM);
  form?.addEventListener('submit', onSubmit);
  if (form) {
    focusableForm.attach(form);
  }
  phoneFormatter.attachPhoneFormatter(Selectors.PHONE_INPUT);

  // Clear checkbox error when user interacts with it
  const smsCheckbox = document.querySelector(Selectors.SMS_CONSENT) as HTMLInputElement;

  if (smsCheckbox) {
    smsCheckbox.addEventListener('change', hideCheckboxError);
  }

  // Clear phone error when user enters a valid 10-digit number
  const phoneInput = document.querySelector(Selectors.PHONE_INPUT) as HTMLInputElement;

  if (phoneInput) {
    phoneInput.addEventListener('input', () => {
      const numericValue = phoneFormatter.stripNonNumeric(phoneInput.value);

      // Only clear error if valid 10-digit number or empty
      if (numericValue.length === 10 || numericValue.length === 0) {
        hidePhoneError();
      }
    });
  }
}

export default {
  init
};
